import { Component, inject, OnInit } from '@angular/core';
import { GridViewProductCard, PriceConfigService, Product, ProductService } from 'gung-standard';
import { JeevesProductGridViewComponent } from 'gung-standard-jeeves';
import { environment } from 'projects/car-liner/src/environments/environment';
import { first, forkJoin, of } from 'rxjs';
import { CarLinerModalService } from '../../services/car-liner-modal.service';
import { CarLinerPriceService } from '../../services/car-liner-price.service';
import { CarLinerProductListByIdsAddonsConfigService } from '../../services/car-liner-product-list-by-ids-addons-config.service';
import { CarLinerProductListConfigService } from '../../services/car-liner-product-list-config.service';

@Component({
  selector: 'car-liner-product-grid-view',
  templateUrl: './car-liner-product-grid-view.component.html',
  styleUrls: ['./car-liner-product-grid-view.component.scss'],
  standalone: false
})
export class CarLinerProductGridViewComponent extends JeevesProductGridViewComponent implements OnInit {
  private carLinerModalService = inject(CarLinerModalService);
  private productService = inject(ProductService);
  private priceConfigService = inject(PriceConfigService);
  private carLinerProductListByIdsAddonsConfigService = inject(CarLinerProductListByIdsAddonsConfigService);
  private carLinerProductListConfigService = inject(CarLinerProductListConfigService);
  private carLinerPriceService = inject(CarLinerPriceService);

  isJosamProject = environment.projectName === 'josam' ? true : false;
  public hidePrice: boolean;
  isAddons: boolean;
  shouldDisplayGrossPrice = false;
  replacementProductNames: { [id: string]: string } = {};

   ngOnInit(): void {
    super.ngOnInit();
    this.evaluateShouldDisplayGrossPrice();
    this.isAddons = this.carLinerProductListByIdsAddonsConfigService.isAddons;
    this.priceConfigService.hidePrice.subscribe(hidePrice => (this.hidePrice = hidePrice));
  }

  ngAfterViewInit(): void {
    // intentionally not emitting renderFinished here — emitted after subscribeToProducts so DOM has full height for scroll restoration
  }

  protected readMappedDataFromCache() {
    super.readMappedDataFromCache();
    setTimeout(() => this.renderFinished?.emit());
  }

  openIncludedItemsModal(product: Product) {
    this.carLinerModalService.openIncludedItemsModal(product);
  }

  protected subscribeToProducts() {
    if (!this.oldFlow || !this.currentFlow || this.oldFlow.id !== this.currentFlow.id) {
      // remove cached data;
      this.keyedMapData = {};
      this.oldFlow = this.currentFlow;
    }
    const newIds = this.data.map(p => p.id).filter(id => !Object.keys(this.keyedMapData).includes(id));
    if (newIds.length === 0) {
      this.readMappedDataFromCache();
      return;
    };

    let addonIds: { [key: string]: string[] } = {};
    let pimRelatedProductsIds: { [key: string]: string[] } = {};
    let powerUnits: { [key: string]: string[] } = {};
    let includedItemIds: { [key: string]: string[] } = {};
    let sparePartsIds: { [key: string]: string[] } = {};
    const replacementProductIds: string[] = [];

    for (const product of this.data) {
      if (product.extra.artv) {
        addonIds[product.id] = product.extra.artv.filter(r => r.q_ej_ecom !== '1').map(r => r.artnrtillvalart);
      }

      if (product.extra.pim && product.extra.pim.relatedProducts) {
        pimRelatedProductsIds[product.id] = product.extra.pim.relatedProducts.split(',');
      }

      if (product?.extra.pim?.powerUnits) {
        powerUnits[product.id] = product.extra.pim.powerUnits.split(',');
      }

      if (product.extra.sr) {
        product.extra.sr.forEach(includedItem => {
          if (includedItem.bartnrstrukt) {
             (includedItemIds[product.id] ??= []).push(includedItem.bartnrstrukt);
          }
        });
      }
      const pimSparePartsIds = product?.extra.pim?.spareParts
        ? product.extra.pim.spareParts.split(',')
        : [];
      sparePartsIds[product.id] = [];
      if (pimSparePartsIds.length > 0) {
        sparePartsIds[product.id].push(...pimSparePartsIds);
      }

      if (product.extra?.ar?.ersatterartnr && !replacementProductIds.includes(product.extra.ar.ersatterartnr)) {
        replacementProductIds.push(product.extra.ar.ersatterartnr);
      }
    }



    this.productService
      .getProductsByIds([])
      .pipe(first())
      .subscribe(includedItemIdsSpareParts => {
        for (const sp of includedItemIdsSpareParts) {
          if (sp.extra.sr.length > 0) {
            for (const sparePart of sp.extra.sr) {
              if (sparePart.bartnrstrukt && !sparePartsIds[sp.id]?.includes(sparePart.bartnrstrukt)) {
                 (sparePartsIds[sp.id] ??= []).push(sparePart.bartnrstrukt);
              }
            }
          }
        }
        
        const allIds = new Set<string>([
          ...Object.values(addonIds).flat(),
          ...Object.values(pimRelatedProductsIds).flat(),
          ...Object.values(powerUnits).flat(),
          ...Object.values(includedItemIds).flat(),
          ...Object.values(sparePartsIds).flat()
        ]);

        const subscription = forkJoin([
          of(this.data),
          this.priceService.getCurrentCustomerPrices(newIds).pipe(first()),
          this.gungFlowService.getSelectedFlow().pipe(first()),
          this.productService.getBlockedProducts(Array.from(allIds)).pipe(first()),
          this.carLinerProductListConfigService.getItems().pipe(first()),
          this.productService.getProductsByIdsUnfiltered(replacementProductIds).pipe(first())
        ])
          .pipe(first())
          .subscribe(data => {
            const prices = data[1];
            const blocked: { [productId: string]: boolean } = data[3];
            const replacementProducts: Product[] = data[5];
            replacementProducts.forEach(p => (this.replacementProductNames[p.id] = p.name));
            let avs = [];
            this.includeAvailability = data[2].useAvailabilities || data[2].requireAvailability;
            if (this.includeAvailability) {
              avs = newIds.map(id => this.getProductAvailability(this.data.find(product => product.id === id)));
            }
            const productsIdsInCurrentAssortment = data[4].map((p: Product) => p.id);

            this.keyedMapData = {
              ...this.keyedMapData,
              ...newIds.reduce((acc, curr) => {
                const product = data[0].find(p => p.id === curr);

                if (!product) {
                  this.findError = true;
                  this.errorMessage += 'No product found. ';
                  throw new Error('No product found');
                }

                const price = prices.filter(p => p && p.productId === curr)[0];
                if (!price) {
                  this.findError = true;
                  this.errorMessage += 'No price found.';
                  throw new Error('No price found');
                }

                const av = avs.filter(p => p && p.productId === curr)[0];
                if (!av && this.includeAvailability) {
                  this.findError = true;
                  this.errorMessage += 'No availability found. ';
                  throw new Error('No availability found');
                }

                const hasAddon = addonIds[product.id]?.some(id => !blocked[id]);
                const hasPimRelated = pimRelatedProductsIds[product.id]?.some(id => !blocked[id]);
                const hasPowerUnits = powerUnits[product.id]?.some(id => !blocked[id]);
                const hasSpareParts = sparePartsIds[product.id]?.some(id => !blocked[id]);
                const hasNotes = !!product.extra.pim?.notes;
                const hasDocuments = product.extra.documents?.length > 0;

                const hasIncluded = includedItemIds[product.id]?.filter(id => productsIdsInCurrentAssortment.includes(id)).some(id => !blocked[id]);

                product.extra._hasInfoIcon = !!(
                  hasAddon ||
                  hasPimRelated ||
                  hasPowerUnits ||
                  hasSpareParts ||
                  hasNotes ||
                  hasDocuments
                );

                product.extra._hasIncludedIcon = !!hasIncluded;

                const item: GridViewProductCard = this.mapItem(curr, product, price, av);
                return {
                  ...acc,
                  [curr]: item
                };
              }, {})
            };
            this.readMappedDataFromCache();
          });
        this.subscriptions.push(subscription);
      });
  }

  evaluateShouldDisplayGrossPrice() {
    const subscription = this.carLinerPriceService.priceMarkupIsEnabled().subscribe(enabled => {
      this.shouldDisplayGrossPrice = enabled;
    });
    this.subscriptions.push(subscription);
  } 
}
